import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Archive, Eye } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CargandoEnLinea } from '@/shared/ui/Cargando';
import { BotonesDeDescarga, descargarInforme, type Formato } from '@/shared/ui/Descargas';
import { PanelLateral } from '@/shared/ui/PanelLateral';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { enFecha } from './fechas';

interface Cajero {
  id: number;
  nombre: string;
  correo: string | null;
}

interface Dia {
  fecha: string;
  cajeroId: number;
  cajero: string;
  recibos: number;
  facturas: number;
  primerRecibo: string | null;
  ultimoRecibo: string | null;
  primeraHora: string | null;
  ultimaHora: string | null;
  efectivo: number;
  transferencia: number;
  tarjeta: number;
  deposito: number;
  otros: number;
  capital: number;
  interes: number;
  total: number;
  anulados: number;
  totalAnulado: number;
}

interface Recibo {
  recibo: string;
  hora: string | null;
  cliente: string;
  identificacion: string;
  medio: string;
  facturas: number;
  capital: number;
  interes: number;
  total: number;
  anulado: boolean;
  motivoAnulacion: string | null;
}

const RUTA = '/cierres/sistema-anterior';

export function CierresDelSistemaAnterior({
  herramientasDeFecha,
  desde,
  hasta,
}: {
  herramientasDeFecha: React.ReactNode;
  desde: string;
  hasta: string;
}) {
  const [cajero, setCajero] = useState('');
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [bajando, setBajando] = useState<Formato | null>(null);
  const [abierto, setAbierto] = useState<Dia | null>(null);

  const cajeros = useQuery({
    queryKey: ['cierres-anteriores', 'cajeros'],
    queryFn: () => api.get<Cajero[]>(`${RUTA}/cajeros`),
  });

  const dias = useQuery({
    queryKey: ['cierres-anteriores', desde, hasta, cajero],
    queryFn: () => api.get<Dia[]>(RUTA, { query: { desde, hasta, ...(cajero ? { cajero } : {}) } }),
  });

  const lista = dias.data ?? [];
  const hoja: Pagina<Dia> | undefined = dias.data && {
    contenido: lista.slice(pagina * tamanio, (pagina + 1) * tamanio),
    pagina,
    tamanio,
    totalElementos: lista.length,
    totalPaginas: Math.max(1, Math.ceil(lista.length / tamanio)),
  };
  const suma = (f: (d: Dia) => number) => lista.reduce((n, d) => n + f(d), 0);

  async function descargar(formato: Formato) {
    setBajando(formato);
    const filtro = new URLSearchParams({ desde, hasta, formato, ...(cajero ? { cajero } : {}) });
    await descargarInforme(
      `${RUTA}/informe?${filtro.toString()}`,
      `cierres-sistema-anterior-${desde}-a-${hasta}.${formato}`,
      'Informe',
    );
    setBajando(null);
  }

  const columnas: Columna<Dia>[] = [
    {
      clave: 'fecha',
      encabezado: 'Día',
      contenido: (d) => <span className="importe text-xs">{enFecha(d.fecha)}</span>,
    },
    {
      clave: 'cajero',
      encabezado: 'Cajero',
      contenido: (d) => (
        <span className="block">
          <span className="block text-xs font-semibold uppercase text-armazon-800">{d.cajero}</span>
          <span className="importe block text-[10px] text-armazon-400">
            {d.primeraHora?.slice(0, 5)} a {d.ultimaHora?.slice(0, 5)}
          </span>
        </span>
      ),
    },
    {
      clave: 'recibos',
      encabezado: 'Recibos',
      alinear: 'derecha',
      contenido: (d) => (
        <span className="block">
          <span className="importe block text-xs">{d.recibos.toLocaleString('es-EC')}</span>
          <span className="importe block text-[10px] text-armazon-400">
            {d.facturas.toLocaleString('es-EC')} facturas
          </span>
        </span>
      ),
      pie: <Cifra valor={suma((d) => d.recibos)} entero />,
    },
    {
      clave: 'efectivo',
      encabezado: 'Efectivo',
      alinear: 'derecha',
      contenido: (d) => <Cifra valor={d.efectivo} />,
      pie: <Cifra valor={suma((d) => d.efectivo)} />,
    },
    {
      clave: 'otros',
      encabezado: 'Transf. · tarjeta · depósito',
      alinear: 'derecha',
      contenido: (d) => <Cifra valor={d.transferencia + d.tarjeta + d.deposito + d.otros} tenue />,
      pie: <Cifra valor={suma((d) => d.transferencia + d.tarjeta + d.deposito + d.otros)} />,
    },
    {
      clave: 'interes',
      encabezado: 'Interés',
      alinear: 'derecha',
      contenido: (d) => <Cifra valor={d.interes} tenue />,
      pie: <Cifra valor={suma((d) => d.interes)} />,
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (d) => (
        <span className="block">
          <Cifra valor={d.total} fuerte />
          {d.anulados > 0 && (
            <span className="block text-[10px] uppercase text-red-600">
              {d.anulados} anulado{d.anulados > 1 ? 's' : ''} · ${d.totalAnulado.toFixed(2)}
            </span>
          )}
        </span>
      ),
      pie: <Cifra valor={suma((d) => d.total)} fuerte />,
    },
    {
      clave: 'ver',
      encabezado: '',
      alinear: 'derecha',
      contenido: (d) => (
        <BotonDeFila icono={Eye} titulo="Ver sus recibos" alPulsar={() => setAbierto(d)} />
      ),
    },
  ];

  return (
    <>
      <p className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[11px] uppercase leading-relaxed text-amber-900">
        <Archive className="mt-0.5 size-4 shrink-0" aria-hidden />
        El sistema anterior no guardaba cierres ni arqueo: cada día se reconstruye de sus pagos, por
        cajero. Es lo que cobró según el sistema, no lo que se contó en el cajón.
      </p>
      <Tabla
        datos={hoja}
        cargando={dias.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(t) => {
          setTamanio(t);
          setPagina(0);
        }}
        entidad="días"
        iconoVacio={Archive}
        mensajeVacio="El sistema anterior no cobró nada en esas fechas"
        herramientas={
          <span className="flex flex-wrap items-center gap-3">
            {herramientasDeFecha}
            <select
              value={cajero}
              onChange={(e) => {
                setCajero(e.target.value);
                setPagina(0);
              }}
              className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase text-armazon-600"
            >
              <option value="">Todos los cajeros</option>
              {(cajeros.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
            <BotonesDeDescarga
              bajando={bajando}
              alDescargar={(f) => void descargar(f)}
              pdf="Los cierres en PDF"
              xlsx="Los cierres en Excel"
            />
          </span>
        }
      />
      {abierto && <PanelDelCierre dia={abierto} alCerrar={() => setAbierto(null)} />}
    </>
  );
}

function PanelDelCierre({ dia, alCerrar }: { dia: Dia; alCerrar: () => void }) {
  const [bajando, setBajando] = useState<Formato | null>(null);
  const recibos = useQuery({
    queryKey: ['cierres-anteriores', 'recibos', dia.fecha, dia.cajeroId],
    queryFn: () => api.get<Recibo[]>(`${RUTA}/${dia.fecha}/${dia.cajeroId}`),
  });

  async function descargar(formato: Formato) {
    setBajando(formato);
    await descargarInforme(
      `${RUTA}/${dia.fecha}/${dia.cajeroId}/informe?formato=${formato}`,
      `cierre-sistema-anterior-${dia.fecha}.${formato}`,
      'Cierre',
    );
    setBajando(null);
  }

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      titulo={`Cierre del ${enFecha(dia.fecha)}`}
      descripcion={`${dia.cajero} · sistema anterior`}
      icono={Archive}
    >
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-armazon-200 bg-armazon-50/60 p-4">
          <span>
            <span className="block text-[10px] font-semibold uppercase text-armazon-400">
              {dia.recibos} recibos · {dia.primerRecibo} a {dia.ultimoRecibo}
            </span>
            <span className="importe block text-lg font-bold text-armazon-900">
              ${dia.total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
            </span>
            <span className="block text-[10px] uppercase text-armazon-500">
              Efectivo ${dia.efectivo.toFixed(2)} · interés ${dia.interes.toFixed(2)}
            </span>
          </span>
          <BotonesDeDescarga
            bajando={bajando}
            alDescargar={(f) => void descargar(f)}
            pdf="El cierre en PDF"
            xlsx="El cierre en Excel"
          />
        </div>
        {recibos.isPending ? (
          <CargandoEnLinea detalle="Trayendo los recibos" />
        ) : (
          <ul className="divide-y divide-armazon-100 rounded-xl border border-armazon-200">
            {(recibos.data ?? []).map((r) => (
              <li
                key={r.recibo}
                className={`flex items-center gap-3 px-3 py-2 text-xs ${r.anulado ? 'bg-red-50/60' : ''}`}
              >
                <span className="importe w-10 shrink-0 text-[10px] text-armazon-400">
                  {r.hora?.slice(0, 5)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold uppercase text-armazon-800">
                    {r.cliente}
                  </span>
                  <span className="importe block text-[10px] text-armazon-400">
                    {r.recibo} · {r.identificacion} · {r.medio} · {r.facturas} fact.
                  </span>
                  {r.anulado && (
                    <span className="block text-[10px] uppercase text-red-700">
                      Anulado{r.motivoAnulacion ? `: ${r.motivoAnulacion}` : ''}
                    </span>
                  )}
                </span>
                <span
                  className={`importe shrink-0 font-semibold ${r.anulado ? 'text-red-400 line-through' : 'text-armazon-900'}`}
                >
                  ${r.total.toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </PanelLateral>
  );
}

function Cifra({
  valor,
  tenue,
  fuerte,
  entero,
}: {
  valor: number;
  tenue?: boolean;
  fuerte?: boolean;
  entero?: boolean;
}) {
  return (
    <span
      className={`importe block text-xs ${fuerte ? 'font-bold text-armazon-900' : tenue ? 'text-armazon-500' : 'text-armazon-700'}`}
    >
      {entero
        ? valor.toLocaleString('es-EC')
        : `$${valor.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
    </span>
  );
}
